import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import useRecipients from "../hooks/useRecipients";
import { Avatar, formatRecipient, icons, matchesQuery, sentAgo } from "../utils/recipients";

/**
 * People (Payee Management POC, REQS-50): the sender's favorites and recent recipients.
 * Tap a person to start a send to them; star a recent one to make it a favorite; rename or
 * remove favorites. Reached from "See all" on the Send screen and from Settings.
 */
const People = () => {
  const navigate = useNavigate();
  const { people, loaded, save, remove } = useRecipients();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // `${type}:${value}` of the row being named
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState("");

  const filtered = useMemo(() => people.filter((p) => matchesQuery(p, query)), [people, query]);
  const favorites = filtered.filter((p) => p.saved);
  const recents = filtered.filter((p) => !p.saved);

  const keyOf = (p) => `${p.type}:${p.value}`;

  const startEdit = (p) => {
    setError("");
    setEditing(keyOf(p));
    setNickname(p.nickname || "");
  };

  const commit = async (p) => {
    setError("");
    try {
      await save({ type: p.type, value: p.value }, nickname);
      setEditing(null);
      setNickname("");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save this contact.");
    }
  };

  const sendTo = (p) => navigate(ROUTE_CONSTANTS.MAIN_PAGE, { state: { recipient: p.value } });

  const renderRow = (p) => {
    const isEditing = editing === keyOf(p);
    return (
      <li key={keyOf(p)} className="np-list-row np-person-row">
        <button type="button" className="np-person-main" onClick={() => sendTo(p)}>
          <Avatar entry={p} />
          <div className="np-list-body">
            <div className="np-list-title">{p.saved ? p.nickname : formatRecipient(p)}</div>
            <div className="np-list-sub">
              {p.saved ? formatRecipient(p) : sentAgo(p.last_sent_at)}
            </div>
          </div>
        </button>
        {p.saved ? (
          <span className="np-person-actions">
            <button type="button" className="np-pill" onClick={() => startEdit(p)}>
              Rename
            </button>
            <button
              type="button"
              className="np-pill np-pill-danger"
              onClick={() => remove(p.uuid).catch(() => setError("Couldn't remove this contact."))}
            >
              Remove
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="np-star-btn"
            aria-label={`Save ${formatRecipient(p)} as a favorite`}
            onClick={() => startEdit(p)}
          >
            {icons.star(false)}
          </button>
        )}
        {isEditing && (
          <div className="np-person-edit">
            <input
              autoFocus
              placeholder="Name, e.g. Mom"
              value={nickname}
              maxLength={100}
              aria-label="Favorite name"
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nickname.trim()) commit(p);
                if (e.key === "Escape") setEditing(null);
              }}
            />
            <button
              type="button"
              className="np-inline-action"
              disabled={!nickname.trim()}
              onClick={() => commit(p)}
            >
              Save
            </button>
            <button type="button" className="np-pill" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        )}
      </li>
    );
  };

  return (
    <main className="page_with_nav" style={{ justifyContent: "flex-start" }}>
      <div className="np-people-search">
        {icons.search}
        <input
          placeholder="Search name, phone or email"
          value={query}
          aria-label="Search people"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div className="np-recipient-error">{error}</div>}

      {loaded && people.length === 0 && (
        <div className="np-card np-card-pad np-people-empty">
          People you send money to will show up here. Star someone to keep them at the front.
        </div>
      )}

      {favorites.length > 0 && (
        <>
          <div className="np-group-header">Favorites</div>
          <ul className="np-card np-people-list">{favorites.map(renderRow)}</ul>
        </>
      )}

      {recents.length > 0 && (
        <>
          <div className="np-group-header">Recent</div>
          <ul className="np-card np-people-list">{recents.map(renderRow)}</ul>
        </>
      )}

      {loaded && people.length > 0 && filtered.length === 0 && (
        <div className="np-people-empty">No one matches "{query}".</div>
      )}
    </main>
  );
};

export default People;
